import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Input } from "@-/dev.improve.haniverse.components.ui.input";
import { Label } from "@-/dev.improve.haniverse.components.ui.label";
import {
  Surface,
  SurfaceContent,
  SurfaceDescription,
  SurfaceHeader,
  SurfaceTitle,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { type TextStyle, View } from "@-/com.npmjs.react-native";
import * as React from "react";

const RESEND_CODE_INTERVAL_SECONDS = 30;

const TABULAR_NUMBERS_STYLE: TextStyle = { fontVariant: ["tabular-nums"] };

export type VerifyEmailFormProps = {
  /** Disables inputs and the primary button while a request is in flight. */
  busy?: boolean;
  /** Controlled verification-token value; falls back to internal state when omitted. */
  code?: string;
  /** Email address the verification code was sent to (shown in the description). */
  email?: string;
  /** Cancel handler (e.g. navigate back to sign up). */
  onCancel?: () => void;
  onChangeCode?: (value: string) => void;
  /** Resend handler. Invoked when the user taps "Resend"; the countdown restarts. */
  onResend?: () => void;
  /** Submit handler. Receives the entered token. */
  onSubmit?: (values: { code: string }) => void;
};

export function VerifyEmailForm({
  busy = false,
  code: codeProp,
  email,
  onCancel,
  onChangeCode,
  onResend,
  onSubmit,
}: VerifyEmailFormProps = {}) {
  const { countdown, restartCountdown } = useCountdown(RESEND_CODE_INTERVAL_SECONDS);

  const [codeState, setCodeState] = React.useState("");
  const code = codeProp ?? codeState;

  function handleChangeCode(value: string) {
    if (onChangeCode != null) {
      onChangeCode(value);
    } else {
      setCodeState(value);
    }
  }

  function handleSubmit() {
    onSubmit?.({ code });
  }

  return (
    <View className="gap-6">
      <Surface className="border-border/0 pb-4 shadow-none sm:border-border sm:shadow-sm sm:shadow-black/5">
        <SurfaceHeader>
          <SurfaceTitle className="text-center text-xl sm:text-left">
            Verify your email
          </SurfaceTitle>
          <SurfaceDescription className="text-center sm:text-left">
            Enter the verification code sent to{" "}
            {email != null && email.length > 0 ? email : "your email"}
          </SurfaceDescription>
        </SurfaceHeader>
        <SurfaceContent className="gap-6">
          <View className="gap-6">
            <View className="gap-1.5">
              <Label htmlFor="code">Verification code</Label>
              <Input
                autoCapitalize="none"
                autoComplete="sms-otp"
                editable={!busy}
                id="code"
                keyboardType="numeric"
                onChangeText={handleChangeCode}
                onSubmitEditing={handleSubmit}
                returnKeyType="send"
                textContentType="oneTimeCode"
                value={code}
              />
              <Button
                disabled={countdown > 0 || busy}
                onPress={() => {
                  onResend?.();
                  restartCountdown();
                }}
                size="sm"
                variant="link"
              >
                <Text className="text-center text-xs">
                  Didn&apos;t receive the code? Resend{" "}
                  {countdown > 0 ? (
                    <Text className="text-xs" style={TABULAR_NUMBERS_STYLE}>
                      ({countdown})
                    </Text>
                  ) : null}
                </Text>
              </Button>
            </View>
            <View className="gap-3">
              <Button className="w-full" disabled={busy} onPress={handleSubmit} variant="link">
                <Text>Continue</Text>
              </Button>
              <Button className="mx-auto" disabled={busy} onPress={onCancel}>
                <Text>Cancel</Text>
              </Button>
            </View>
          </View>
        </SurfaceContent>
      </Surface>
    </View>
  );
}

function useCountdown(seconds = 30) {
  const [countdown, setCountdown] = React.useState(seconds);
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const stopCountdown = React.useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startCountdown = React.useCallback(() => {
    stopCountdown();
    setCountdown(seconds);

    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          stopCountdown();
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  }, [seconds, stopCountdown]);

  React.useEffect(() => {
    startCountdown();

    return stopCountdown;
  }, [startCountdown, stopCountdown]);

  return { countdown, restartCountdown: startCountdown };
}
